import React from 'react';
import { Link } from 'react-router-dom';
import { Claim, canShow } from './Claim';

/**
 * PublicFooterV2 — the V2 public site footer.
 *
 * Carries Privacy Policy and Terms links. The audit flagged their absence as a
 * launch blocker rather than a nicety: the site fingerprints visitors via
 * utils/tracker.ts and stores identifiers in localStorage, so shipping without a
 * privacy policy is a compliance exposure, not a missing page.
 *
 * All copy resolves through the claims registry, so nothing unverified can be
 * added here later by editing markup.
 */

interface FooterGroup {
  readonly heading: string;
  readonly links: readonly { readonly label: string; readonly to: string }[];
}

/**
 * Footer destinations.
 *
 * FIXED IN 1.10, AND IT WAS MY BUG: these were written as root paths (/platform,
 * /proof, /services/...) while every V2 page is mounted under /v2. Nine of the
 * thirteen links 404'd. The original tests asserted the links rendered, which
 * they did -- to nowhere. `FooterLinkResolution` in the footer test now checks
 * every entry against the real route table instead.
 *
 * Two entries were removed rather than repaired:
 *   - "Terms", which pointed at /terms. No terms page exists and drafting one is
 *     legal work, not something to invent to satisfy a link.
 *   - "Evidence classes", which pointed at /proof#evidence. No such anchor
 *     exists; the Proof Room link covers it.
 * A link to a policy that does not exist is worse than no link, because it
 * implies a document someone has written.
 */
const GROUPS: readonly FooterGroup[] = [
  {
    heading: 'Services',
    links: [
      { label: 'Opportunity Sprint', to: '/services/ai-opportunity-sprint' },
      { label: 'Production Pilot', to: '/services/claude-production-pilot' },
      { label: 'Build & Modernization', to: '/services/enterprise-build-modernization' },
      { label: 'Workforce Accelerator', to: '/services/workforce-architect-accelerator' },
      { label: 'Embedded AI Ops', to: '/services/embedded-ai-operations' },
    ],
  },
  {
    heading: 'Platform',
    links: [
      { label: 'Platform', to: '/platform' },
      { label: 'Free workspace', to: '/free-workspace' },
      { label: 'Pricing', to: '/pricing' },
    ],
  },
  {
    heading: 'Proof',
    links: [
      /*
       * ONE ENTRY, NOT TWO. This list carried "Proof Room" -> `/proof` and
       * "Builder stories" -> `/stories` as separate destinations. They are the
       * same page now - the records took `/proof` over and `/stories` redirects
       * to it - so keeping both would put a link and its own redirect side by
       * side under one heading.
       *
       * "Case studies" rather than "Proof Room" or "Builder stories", because
       * this entry exists to be FOUND. Ali, 2026-09-04: "so Case Studies can be
       * reviewed by people visiting our website." A visitor scanning a footer
       * for case studies should not have to guess that a room, or a builder, is
       * where they are kept.
       *
       * `/proof` and NOT `/stories/<a-slug>`. The detail route is a pattern,
       * because a published record has no fixed address the way a service page
       * does. Site chrome may only link a stable destination: a footer pointing
       * at one record becomes a dead link in the chrome of every page the day
       * that record is unpublished, and unpublishing is a normal operation here
       * (spec section 35), not an incident. The index links every published
       * record, so the detail pages are one click away from a URL that cannot
       * rot. `consentAndSeo.test.tsx` fails if a `/stories/...` link ever
       * appears in this list.
       */
      { label: 'Case studies', to: '/proof' },
      { label: 'Map an opportunity', to: '/lab' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'Contact', to: '/contact' },
      { label: 'What we collect', to: '/privacy' },
    ],
  },
];

/** Exported so the test can check each destination resolves to a real route. */
export const FOOTER_LINKS: readonly string[] = GROUPS.flatMap((g) => g.links.map((l) => l.to));

function PublicFooterV2(): React.ReactElement {
  const year = new Date().getFullYear();

  return (
    <footer className="cbv2-footer">
      <div className="cbv2-wrap">
        <div className="cbv2-footer__grid">
          <div>
            <div className="cbv2-footer__brand">
              <img src="/colaberry-logo-transparent.png" alt="Colaberry" width={291} height={82} />
            </div>
            <p style={{ fontSize: 'var(--fs-caption)', maxWidth: '32ch', margin: 0 }}>
              <Claim claimKey="positioning.primary" />
            </p>
            {canShow('anthropic.capability') ? (
              <p
                style={{
                  fontSize: 'var(--fs-caption)',
                  marginTop: 'var(--space-3)',
                  opacity: 0.8,
                }}
              >
                <Claim claimKey="anthropic.capability" />
              </p>
            ) : null}
          </div>

          {GROUPS.map((group) => (
            <div key={group.heading}>
              <h2>{group.heading}</h2>
              <ul>
                {group.links.map((link) => (
                  <li key={`${group.heading}-${link.to}-${link.label}`}>
                    <Link to={link.to}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="cbv2-footer__bottom">
          <span>&copy; {year} Colaberry Inc. All rights reserved.</span>
          <span>
            <a href="mailto:info@colaberry.com">info@colaberry.com</a>
          </span>
        </div>
      </div>
    </footer>
  );
}

export default PublicFooterV2;
